/** Simple employer */
interface SimpleEmployer extends Omit<ApiSchema<'EmployerRead2'>, 'id' | 'name' | 'main' | 'confidential'> {}

/** Simple employer profile list */
export interface SimpleEmployerProfileList extends SimpleEmployer {
  id: string;
  name: string;
  radioValue: string;
  main?: boolean;
  confidential?: boolean;
  fromSearch?: boolean;
  isSearchButton?: boolean;
  disabledReason?: string;
}

/** Mutate profile list function */
type MutateEmployerProfileListFn = (profileList?: Awaited<ApiSchema<'EmployerRead2'>[]>) => SimpleEmployerProfileList[] | [];

/** Find initial employer function */
type FindInitialEmployer = (profileList?: SimpleEmployerProfileList[], name?: string) => string;

/** Mutate employer profile function */
type MutateProfileInListFn = (
  profileList?: SimpleEmployerProfileList[],
  name?: string,
  mutation?: Partial<SimpleEmployerProfileList>,
) => SimpleEmployerProfileList[] | [];

/**
 * @description Mutate employer profile list. Add Confidential, Search Employer button and From Search objects.
 * - `profileList` - The employer profile list.
 */
export const mutateEmployerProfileList: MutateEmployerProfileListFn = (profileList) => {
  if (!profileList) return [];

  // Value holder
  const mutatedProfileList: SimpleEmployerProfileList[] = [];

  // Simplify the profile list
  profileList.forEach((employer, index) => {
    const name = employer?.name ?? '';
    const id = String(employer?.id) ?? `${name || 'n'}-${index}`;
    const radioValue = name || '';

    mutatedProfileList.push({
      ...employer,
      id,
      name,
      radioValue,
    });
  });

  // Add Confidential employer. It should be the second item in the list.
  mutatedProfileList.splice(1, 0, {
    id: 'confidential',
    name: 'Confidential',
    radioValue: 'confidential',
    confidential: true,
    disabledReason: 'global.label.premium_feature',
  });

  // Add fromSearch. Mutate this with value from search modal.
  // Add search employer button (opens modal). It should be the last item in the list.
  mutatedProfileList.push({
    id: 'from-search',
    name: '',
    radioValue: '',
    fromSearch: true,
  }, {
    id: 'search-employer-button',
    name: 'searchEmployerButton',
    radioValue: '',
    isSearchButton: true,
  });

  return mutatedProfileList;
};


/**
 * @description Find the initial employer name from the profile list. API sends the name, but the value is not always the same.
 * - `profileList` - The mutated employer profile list.
 */
export const findInitialEmployer: FindInitialEmployer = (profileList, name) => {
  if (!profileList) return 'main';
  const mainEmployer = profileList.find((employer) => employer.main);
  const initialEmployer = profileList.find((employer) => employer.name === name);
  return initialEmployer?.radioValue || mainEmployer?.radioValue || 'main';
};


/**
 * @description Mutate employer profile in list. Used to update the employer profile in the list.
 * - `profileList` - The employer profile list.
 * - `name` - The name of the employer profile.
 * - `mutation` - The mutation to apply to the employer profile.
 */
export const mutateProfileInList: MutateProfileInListFn = (profileList, name, mutation) => {
  if (!profileList) return [];
  if (!name || !mutation) return profileList;

  return profileList
    .map((profile) => (profile.name === name ? { ...profile, ...mutation } : profile));
};
